/**
 * Runtime content model (tech-design §3). The validated JSON packs are flattened
 * into id→entity lookups the engine reads during ticks and command handling.
 *
 * `core` imports only content **types** (`import type`), never the Zod runtime —
 * the layer boundary that keeps the engine headless and the schema the single
 * source of truth.
 */
import type {
  Age,
  Breakthrough,
  Generator as GeneratorDef,
  GlobalPack,
  Milestone,
  Pack,
  PrestigeLayer,
  Resource,
  Trait,
  Upgrade,
} from '@content/index.js';

export type { GeneratorDef };

/** Indexed, engine-ready view of every loaded pack. */
export interface Content {
  readonly ages: ReadonlyMap<string, Age>;
  readonly resources: ReadonlyMap<string, Resource>;
  readonly generators: ReadonlyMap<string, GeneratorDef>;
  readonly upgrades: ReadonlyMap<string, Upgrade>;
  readonly traits: ReadonlyMap<string, Trait>;
  readonly breakthroughs: ReadonlyMap<string, Breakthrough>;
  readonly milestones: ReadonlyMap<string, Milestone>;
  readonly prestige: ReadonlyMap<string, PrestigeLayer>;
}

function index<T extends { readonly id: string }>(
  items: readonly T[],
): ReadonlyMap<string, T> {
  return new Map(items.map((item) => [item.id, item]));
}

/** The empty content bundle — what the M0 walking skeleton runs against. */
export const EMPTY_CONTENT: Content = {
  ages: new Map(),
  resources: new Map(),
  generators: new Map(),
  upgrades: new Map(),
  traits: new Map(),
  breakthroughs: new Map(),
  milestones: new Map(),
  prestige: new Map(),
};

/** Flatten validated packs (+ optional global pack) into the indexed {@link Content}. */
export function buildContent(packs: readonly Pack[], global?: GlobalPack): Content {
  const milestones: Milestone[] = [];
  const resources: Resource[] = [];
  const generators: GeneratorDef[] = [];
  const upgrades: Upgrade[] = [];
  const traits: Trait[] = [];
  const breakthroughs: Breakthrough[] = [];
  const ages: Age[] = [];

  for (const pack of packs) {
    ages.push(pack.age);
    resources.push(...pack.resources);
    generators.push(...pack.generators);
    upgrades.push(...pack.upgrades);
    traits.push(...pack.traits);
    breakthroughs.push(...pack.breakthroughs);
    milestones.push(...pack.milestones);
  }
  if (global) {
    milestones.push(...global.milestones);
  }

  return {
    ages: index(ages),
    resources: index(resources),
    generators: index(generators),
    upgrades: index(upgrades),
    traits: index(traits),
    breakthroughs: index(breakthroughs),
    milestones: index(milestones),
    prestige: index(global?.prestige ?? []),
  };
}
